import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Heading,
  VStack,
  HStack,
  Text,
  Spinner,
  Center,
  CheckboxGroup,
  Checkbox,
  Stack,
  Button,
  ButtonGroup,
  SimpleGrid,
  useColorModeValue,
  Flex,
  Spacer,
  RadioGroup,
  Radio,
  Select,
  Switch,
  FormControl,
  FormLabel,
  Wrap,
  IconButton, // Import IconButton
  Collapse, // Import Collapse
} from "@chakra-ui/react";
import { ChevronLeftIcon, SettingsIcon } from "@chakra-ui/icons"; // Import SettingsIcon
import { useEnvData, EnvDataProvider } from "../../context/EnvDataContext";
import { authFetch } from "../../utils/authFetch";
import MetricDisplay from "./dashboard/MetricDisplay";

export default function SingleDeviceDashboard() {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const { devicesData } = useEnvData();

  const [deviceConfig, setDeviceConfig] = useState(null);
  const [hourlyData, setHourlyData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- REQ 1 & 3: State สำหรับส่วนควบคุม ---
  const [chartType, setChartType] = useState("line");
  const [selectedPollutants, setSelectedPollutants] = useState([
    "pm25",
    "pm10",
    "tsp",
    "noise",
  ]); // REQ 1: Default เลือกทุกอย่าง
  const [numColumns, setNumColumns] = useState("3"); // REQ 2: Default 3 คอลัมน์
  const [showAvg, setShowAvg] = useState(true);
  const [showMin, setShowMin] = useState(false);
  const [showMax, setShowMax] = useState(false);

  const [showControls, setShowControls] = useState(true);

  const controlPanelBg = useColorModeValue("gray.50", "gray.500");
  // REQ 1: เอา temp/humid ออก
  const availablePollutants = useMemo(
    () => [
      { key: "pm25", label: "PM2.5" },
      { key: "pm10", label: "PM10" },
      { key: "tsp", label: "TSP" },
      { key: "noise", label: "Noise" },
    ],
    []
  );

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem("token");
        const headers = { headers: { Authorization: `Bearer ${token}` } };

        const configRes = await authFetch(
          `${process.env.REACT_APP_API_URL}/api/devices/${deviceId}`,
          headers
        );
        const configData = await configRes.json();
        if (!configRes.ok)
          throw new Error(configData.error || "Failed to fetch device config");
        setDeviceConfig(configData);

        // REQ 3: ตั้งค่า default ของ switch จาก database
        setShowAvg(configData.showAverage);
        setShowMin(configData.showMin);
        setShowMax(configData.showMax);

        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const hourlyRes = await authFetch(
          `${
            process.env.REACT_APP_API_URL
          }/api/data/hourly?deviceId=${deviceId}&start=${todayStart.toISOString()}&end=${new Date().toISOString()}`,
          headers
        );
        const hourlyData = await hourlyRes.json();
        if (!hourlyRes.ok)
          throw new Error(hourlyData.error || "Failed to fetch hourly data");
        setHourlyData(hourlyData);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [deviceId]);

  // --- เพิ่มการคำนวณจำนวนคอลัมน์ที่จะแสดงผลจริง ---
  const effectiveColumns = useMemo(() => {
    return Math.min(parseInt(numColumns), selectedPollutants.length);
  }, [numColumns, selectedPollutants]);

  if (isLoading)
    return (
      <Center h="80vh">
        <Spinner size="xl" />
      </Center>
    );
  if (error)
    return (
      <Center h="80vh">
        <Text color="red.500">Error: {error}</Text>
      </Center>
    );

  return (
    <Box maxW="container.2xl" mx="auto" p={5}>
      <VStack align="stretch" spacing={6}>
        {/* REQ 5: Back Button and Header */}
        <Flex align="center" mb={2}>
          <Button
            onClick={() => navigate(-1)}
            leftIcon={<ChevronLeftIcon />}
            variant="ghost"
          >
            Back
          </Button>
          <Spacer />
          <Heading textAlign="center" size="lg">
            Dashboard: {deviceId}
          </Heading>
          <Spacer />
          {/* --- REQ 2: ปุ่มสำหรับ Toggle --- */}
          <Box onClick={() => setShowControls(!showControls)}>
            {showControls ? (
              <Text>[ - ] Hide Control Panel</Text>
            ) : (
              <Text>[ + ] Show Control Panel</Text>
            )}
          </Box>
        </Flex>

        {/* --- REQ 4: Control Panel Layout --- */}
        <Collapse in={showControls} animateOpacity>
          <Wrap
            p={4}
            bg={controlPanelBg}
            borderRadius="lg"
            spacingX={8} // ระยะห่างแนวนอน
            spacingY={4} // ระยะห่างแนวตั้งเมื่อขึ้นบรรทัดใหม่
            align="center"
            shadow="sm"
            border="1px"
            spacing={20}
            borderColor={controlPanelBg}
          >
            <FormControl w="auto">
              <FormLabel fontWeight="bold" m={0} mb={2}>
                Metrics
              </FormLabel>
              <CheckboxGroup
                colorScheme="green"
                value={selectedPollutants}
                onChange={setSelectedPollutants}
              >
                <Stack direction="row" spacing={5}>
                  {availablePollutants.map((p) => (
                    <Checkbox key={p.key} value={p.key}>
                      {p.label}
                    </Checkbox>
                  ))}
                </Stack>
              </CheckboxGroup>
            </FormControl>

            <FormControl w="auto">
              <FormLabel fontWeight="bold" m={0} mb={2}>
                Series
              </FormLabel>
              <HStack>
                <Switch
                  size="md"
                  isChecked={showAvg}
                  onChange={(e) => setShowAvg(e.target.checked)}
                >
                  Avg
                </Switch>
                <Switch
                  size="md"
                  isChecked={showMax}
                  onChange={(e) => setShowMax(e.target.checked)}
                >
                  Max
                </Switch>
                <Switch
                  size="md"
                  isChecked={showMin}
                  onChange={(e) => setShowMin(e.target.checked)}
                >
                  Min
                </Switch>
              </HStack>
            </FormControl>

            <FormControl w="auto">
              <FormLabel fontWeight="bold" m={0} mb={2}>
                Chart Type
              </FormLabel>
              <ButtonGroup variant="outline" size="sm" isAttached>
                <Button
                  isActive={chartType === "line"}
                  onClick={() => setChartType("line")}
                >
                  Line
                </Button>
                <Button
                  isActive={chartType === "area"}
                  onClick={() => setChartType("area")}
                >
                  Area
                </Button>
                <Button
                  isActive={chartType === "bar"}
                  onClick={() => setChartType("bar")}
                >
                  Bar
                </Button>
              </ButtonGroup>
            </FormControl>

            <FormControl w="auto">
              <FormLabel fontWeight="bold" m={0} mb={2}>
                Columns
              </FormLabel>
              <Select
                size="sm"
                w="120px"
                value={numColumns}
                onChange={(e) => setNumColumns(e.target.value)}
                borderRadius="md"
              >
                <option value="1">1 Column</option>
                <option value="2">2 Columns</option>
                <option value="3">3 Columns</option>
                <option value="4">4 Columns</option>
              </Select>
            </FormControl>
          </Wrap>
        </Collapse>

        {/* --- REQ 2: Dynamic Display Area --- */}
        <Box>
          {selectedPollutants.length > 0 ? (
            <SimpleGrid
              columns={{
                base: 1,
                md: Math.min(2, effectiveColumns),
                lg: effectiveColumns,
              }}
              spacing={6}
            >
              {selectedPollutants.map((pollutantKey) => (
                <MetricDisplay
                  key={pollutantKey}
                  metric={availablePollutants.find(
                    (p) => p.key === pollutantKey
                  )}
                  realtimeData={devicesData[deviceId]?.sensorData}
                  hourlyData={hourlyData}
                  chartType={chartType}
                  showAvg={showAvg}
                  showMin={showMin}
                  showMax={showMax}
                  // --- ส่ง prop จำนวนคอลัมน์ลงไป ---
                  columnCount={effectiveColumns}
                />
              ))}
            </SimpleGrid>
          ) : (
            <Center p={10} bg="gray.50" borderRadius="md">
              <Text>Please select a metric to display.</Text>
            </Center>
          )}
        </Box>
      </VStack>
    </Box>
  );
}
